/** Training Analytics — Payments log: every course payment in the date range, searchable, CSV download. */
import React, { useEffect, useState } from 'react';
import { Download, Loader2, Search } from 'lucide-react';
import { toast } from 'sonner';
import API_URL from '@/config/api';
import { Card } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { gbp } from '@/components/training/analytics/shared';

const fmt = (iso) => new Date(iso).toLocaleString('en-GB', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
const fmtD = (d) => (d ? new Date(d).toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }) : '');

export function PaymentsLog({ start, end }) {
  const [q, setQ] = useState('');
  const [log, setLog] = useState(null);
  const [busy, setBusy] = useState(false);
  const qs = () => new URLSearchParams({ start_date: start, end_date: end, q });
  useEffect(() => {
    if (!start || !end) return;
    const t = setTimeout(async () => {
      setBusy(true);
      try {
        const res = await fetch(`${API_URL}/api/staff/training-analytics/payments?${qs()}`, { credentials: 'include' });
        if (!res.ok) throw new Error((await res.json().catch(() => ({}))).detail || 'Could not load payments');
        setLog(await res.json());
      } catch (e) { toast.error(e.message); } finally { setBusy(false); }
    }, 250);
    return () => clearTimeout(t);
  }, [start, end, q]); // eslint-disable-line react-hooks/exhaustive-deps
  const download = async () => {
    try {
      const res = await fetch(`${API_URL}/api/staff/training-analytics/payments.csv?${qs()}`, { credentials: 'include' });
      if (!res.ok) throw new Error('Download failed');
      const url = URL.createObjectURL(await res.blob());
      const a = Object.assign(document.createElement('a'), { href: url, download: `training-payments_${start}_to_${end}.csv` });
      a.click(); URL.revokeObjectURL(url);
    } catch (e) { toast.error(e.message); }
  };
  return (
    <Card className="overflow-hidden" data-testid="payments-log">
      <div className="p-3 border-b flex flex-wrap items-center gap-2">
        <div className="flex items-center gap-2 flex-1 min-w-[12rem]"><Search className="w-4 h-4 text-gray-400" /><Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search attendee, course, method…" className="h-9 border-0 shadow-none focus-visible:ring-0" data-testid="payments-search" /></div>
        {log && <div className="text-sm text-gray-600" data-testid="payments-summary"><span className="font-bold text-gray-900" data-testid="payments-total">{gbp(log.total)}</span> across {log.count} payment{log.count === 1 ? '' : 's'}{log.refunds ? ` · ${gbp(log.refunds)} refunded` : ''}</div>}
        <Button size="sm" variant="outline" onClick={download} disabled={!log?.count} data-testid="payments-download"><Download className="w-4 h-4 mr-1.5" /> Download CSV</Button>
      </div>
      {log?.by_method?.length > 0 && <div className="px-3 py-2 border-b bg-gray-50 flex flex-wrap gap-2 text-xs" data-testid="payments-by-method">{log.by_method.map((m) => <span key={m.method} className="px-2 py-1 rounded-full bg-white border border-gray-200 text-gray-700"><b>{m.method}</b> {gbp(m.total)} · {m.count}</span>)}</div>}
      <div className="max-h-[70vh] overflow-auto">
        {busy && !log && <div className="p-6 text-center"><Loader2 className="w-5 h-5 animate-spin inline text-gray-400" /></div>}
        {log && !log.rows.length && <p className="p-6 text-sm text-gray-500 text-center" data-testid="payments-empty">No payments dated in this range.</p>}
        {log?.rows.length > 0 && (
          <div className="table-scroll"><table className="w-full text-sm" data-testid="payments-table">
            <thead className="sticky top-0 bg-gray-50 text-[11px] uppercase tracking-wider text-gray-500"><tr><th className="text-left px-3 py-2">Date</th><th className="text-left px-3 py-2">Attendee</th><th className="text-left px-3 py-2 hidden md:table-cell">Course</th><th className="text-right px-3 py-2">Amount</th><th className="text-left px-3 py-2 hidden sm:table-cell">Method</th><th className="text-left px-3 py-2 hidden lg:table-cell">Recorded by</th><th className="text-right px-3 py-2">Running</th></tr></thead>
            <tbody>
              {log.rows.map((x) => (
                <tr key={x.id} className="border-t border-gray-100" data-testid={`payment-row-${x.id}`}>
                  <td className="px-3 py-2 whitespace-nowrap text-gray-700">{fmt(x.at)}</td>
                  <td className="px-3 py-2"><div className="font-semibold text-gray-900">{x.attendee}</div><div className="text-xs text-gray-500">{x.company || x.email || ''}</div></td>
                  <td className="px-3 py-2 hidden md:table-cell"><div className="text-gray-800">{x.course}</div><div className="text-xs text-gray-500">{x.brand}{x.course_date ? ` · ${fmtD(x.course_date)}` : ''}</div></td>
                  <td className={`px-3 py-2 text-right font-bold ${x.amount < 0 ? 'text-red-700' : 'text-gray-900'}`}>{gbp(x.amount)}</td>
                  <td className="px-3 py-2 hidden sm:table-cell text-gray-700">{x.method || <span className="text-gray-400">—</span>}{x.source && <div className="text-[11px] text-gray-400 capitalize">{x.source}</div>}</td>
                  <td className="px-3 py-2 hidden lg:table-cell text-xs text-gray-600">{x.recorded_by}</td>
                  <td className="px-3 py-2 text-right text-gray-600">{gbp(x.running_total)}</td>
                </tr>
              ))}
            </tbody>
          </table></div>
        )}
      </div>
    </Card>
  );
}
